import type { FulfillmentOrderLine } from "../types";
import type { EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { normalized, roles, factors, unit, EntryParseError, parseEntryDates, parseEntrySegments } from "./confirmationEntryPrimitives";
import { calculateFulfillmentLineAmount, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";

/** 本分类显式决定日期来源，日期范围不隐式参与计次数量。 */
function datePatch(input: EntryParseBaseInput, range = input.range): Partial<FulfillmentOrderLine> {
  return parseEntryDates(input.routeDays, range, "服务", "text");
}

/** 本分类服务主体资格与结算主体分别保留。 */
function providerPatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  const supplier = input.provider && "id" in input.provider ? input.provider : null;
  if (supplier && "snapshotResourceType" in supplier) {
    if (supplier.snapshotResourceType !== "餐") throw new EntryParseError(`服务主体“${supplier.name}”属于${supplier.snapshotResourceType}，不能用于餐；请重新选择本类服务主体`, "provider");
  } else if (supplier) {
    if (!supplier.canBeFulfillmentSubject) throw new EntryParseError(`服务主体“${supplier.name}”不具备当前履约资格，请重新选择可履约的主体`, "provider");
  }
  return {
    fulfillmentSupplierId: supplier?.id ?? null,
    fulfillmentSupplierName: supplier?.name ?? null,
    settlementSupplierId: input.settlement?.id ?? null,
    settlementSupplierName: input.settlement?.name ?? null,
  };
}
/** 本分类明确资源类型和计价单位后校验完整行。 */
function basePatch(input: EntryParseBaseInput, name: string, quantity: string, count: string, price: string, quantityUnit: string, extra: Partial<FulfillmentOrderLine> = {}): Partial<FulfillmentOrderLine> {
  const amount = calculateFulfillmentLineAmount(quantity, count, price);
  const patch: Partial<FulfillmentOrderLine> = {
    resourceType: "餐", itemNameSnapshot: name, itemSpecSnapshot: null,
    quantity, billingCount: count, unitPriceAmount: price, amount, unit: quantityUnit,
    pricingUnitType: "person_meal",
    reviewStatus: "confirmed", sourceType: "manual", status: "active", ...datePatch(input), ...providerPatch(input), ...extra,
  };
  const errors = validateFulfillmentLineDraft({ ...patch, id: input.allowZeroQuantity ? "existing-preview" : "local-preview" } as FulfillmentOrderLine);
  if (errors.length) throw new Error(errors.join("；"));
  return patch;
}
/** 解析本类单段原文，保留本类资源和计费约束。 */
function parseSegment(input: EntryParseBaseInput, text: string, preserveRange: boolean): Partial<FulfillmentOrderLine>[] {
  const raw = normalized(text);
  const match = raw.match(/^([dD\d,，-]+)\s+(.+)$/);
  if (!match) throw new EntryParseError("餐费缺少行首日期，请在文本开头填写实际出行日，例如1 中49人×20；日期以文本为准", "range", "text");
  const dates = datePatch(input, match[1]);
  return match[2].split(/[,，、]/).flatMap((part) => {
    const meal = part.trim().match(new RegExp(`^(早餐|午餐|中餐|晚餐|早|中|晚)\\s*(?:(${roles})\\s*)?(.+)$`));
    if (!meal) throw new Error("请填早/中/晚和人数或桌数");
    const p = factors(meal[3], undefined, input.allowZeroQuantity);
    if (![2, 3].includes(p.length) || !["人", "桌"].includes(p[0].unit)) throw new Error("餐费须用人/桌区分，例如49人×20或4桌×300");
    if (!preserveRange && p.length === 3 && dates.dayNumbers!.length > 1) throw new Error("多日范围逐日生成，不再重复填写餐数");
    if (p.length === 3) unit(p[1], ["餐", "次"]);
    if (p[0].unit === "桌" && meal[2]) throw new Error("桌餐不使用旅客角色人数，请保留实际桌数");
    const name = /^(早|早餐)$/.test(meal[1]) ? "早餐" : /^(晚|晚餐)$/.test(meal[1]) ? "晚餐" : "中餐";
    const ranges = preserveRange ? [match[1]] : dates.dayNumbers!.map(String);
    return ranges.map(range => basePatch({ ...input, range }, name, p[0].value, p.length === 3 ? p[1].value : "1", p.at(-1)!.value, p[0].unit,
      { ...datePatch(input, range), mealType: name, travelerRole: meal[2]?.replace("教师", "老师") || null, itemSpecSnapshot: p[0].unit === "桌" ? "按桌计费" : "按人计费" }));
  });
}

/** 新增餐费逐日生成；仅已有汇总行的适配器可保留原范围和显式计费次数。 */
export function parseMealEntry(input: EntryParseBaseInput, { preserveRange = false }: { preserveRange?: boolean } = {}): EntryParseResult {
  return parseEntrySegments(input.text, text => parseSegment(input, text, preserveRange), patch => [...(!patch.settlementSupplierId ? ["结算主体待设置"] : []), ...(!patch.fulfillmentSupplierId ? ["服务主体待核对"] : [])]);
}
